import { Injectable, Inject, DOCUMENT } from '@angular/core';

import { StorageApiService } from 'src/app/api/services/storage-api.service';

export interface DownloadingStorageFile {
  name: string;
  rejectedUploadMessage?: string;
  cancelled?: boolean;
  currentFile?: string;
}

export interface FileRetrieverResponse {
  userData: {
    objectId: string;
  };
  data: any;
  size: number;
}

@Injectable({
  providedIn: 'root',
})
export class DownloadService {
  rejectedUploads: DownloadingStorageFile[] = [];
  activeFolderDownloads: DownloadingStorageFile[] = [];

  private downloadCount = 0;
  private iframeContainer: HTMLDivElement;

  constructor(
    private storageApiService: StorageApiService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.initializeIframeContainer();
  }

  private initializeIframeContainer(): void {
    this.iframeContainer = this.document.createElement('div');
    this.iframeContainer.style.display = 'none';
    this.document.body.appendChild(this.iframeContainer);
  }

  private downloadURL(url: string, fileName: string): void {
    const hiddenIFrameID = 'hiddenDownloader' + this.downloadCount++;
    const iframe = this.document.createElement('iframe');
    iframe.id = hiddenIFrameID;
    iframe.style.display = 'none';
    this.iframeContainer.appendChild(iframe);

    // https://cloud.google.com/storage/docs/xml-api/reference-headers#responsecontentdisposition
    iframe.src =
      url +
      '&response-content-disposition=attachment%3B%20filename%3D%22' +
      fileName +
      '%22';
  }

  async downloadFile(file: DownloadingStorageFile): Promise<void> {
    try {
      const resp = await this.storageApiService.getSignedDownloadURI(file);

      let downloadName = file.name.replace('--TRASH--/', '');

      if (downloadName.indexOf('/') >= 0) {
        downloadName = downloadName.substr(downloadName.lastIndexOf('/') + 1);
      }

      this.downloadURL(
        resp.message,
        encodeURIComponent(downloadName.replace(/\"/g, '\\"')),
      );
    } catch (e) {
      const error = (e && e.result && e.result.error) || {};

      file.rejectedUploadMessage = error.message;
      this.rejectedUploads.push(file);
      throw e;
    }
  }

  async downloadFolder(folder: DownloadingStorageFile): Promise<void> {
    folder.cancelled = false;
    folder.currentFile = null;
    this.activeFolderDownloads.push(folder);

    try {
      const resp = await this.storageApiService.getFolderContents(folder.name);
      const JSZip = await import('jszip');
      const zip = new (JSZip as any).default() as typeof JSZip;

      for (const file of resp.items) {
        if (!folder.cancelled) {
          if (file.folder) {
            zip.folder(file.objectId);
          } else {
            const response = await this.retrieveFile(file.signedURL, file);
            folder.currentFile = file.objectId;
            zip.file(response.userData.objectId, response.data, {
              binary: true,
            });
          }
        }
      }

      if (!folder.cancelled) {
        const blob = await zip.generateAsync({
          type: 'blob',
        });

        this.activeFolderDownloads.splice(
          this.activeFolderDownloads.indexOf(folder),
          1,
        );
        this.downloadBlob(blob, folder.name.slice(0, -1) + '.zip');
      }
    } catch (e) {
      console.error('Failed to download folder', e);
      this.activeFolderDownloads.splice(
        this.activeFolderDownloads.indexOf(folder),
        1,
      );
      throw e;
    }
  }

  private async retrieveFile(
    signedURL: string,
    file: any,
  ): Promise<FileRetrieverResponse> {
    const response = await fetch(signedURL);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.blob();
    const size = Number(response.headers.get('Content-Length')) || data.size;

    return {
      userData: {
        objectId: file.objectId,
      },
      data,
      size,
    };
  }

  downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = this.document.createElement('a');
    link.style.display = 'none';
    link.href = url;
    link.download = fileName;
    this.document.body.appendChild(link);
    link.click();
    this.document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async downloadFileFromUrl(url: string, fileName: string): Promise<void> {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      this.downloadBlob(blob, fileName);
    } catch (error) {
      console.error('Failed to download file from URL', error);
      throw error;
    }
  }

  cleanUp(): void {
    if (this.iframeContainer) {
      this.iframeContainer.innerHTML = '';
      this.downloadCount = 0;
    }
  }

  cancelFolderDownload(folder: DownloadingStorageFile): void {
    folder.cancelled = true;
    this.activeFolderDownloads.splice(
      this.activeFolderDownloads.indexOf(folder),
      1,
    );
  }
}
